import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EmptyState } from "../../molecules/EmptyState";
import { KgCatalogSection } from "../../molecules/KgCatalogSection";
import { KgCanvasFilterBar } from "../KgCanvasFilterBar";
import { getKgCatalogSectionIcon } from "../../kg/catalog/kgCatalogSectionIcons";
import { KG_CATALOG_SECTIONS, resolveCatalogSectionId } from "../../../services/kg/kgCatalog.constants";
import {
  getKgCatalogSectionEmptyMessage,
  groupNodesByCatalogSection,
  hasAnyCatalogNodes,
} from "../../../services/kg/kgCatalogMappers";
import type { KgCatalogSectionId } from "../../../services/kg/kgCatalog.types";
import type { KgCatalogProps } from "./KgCatalog.types";
import {
  LoadMoreButton,
  LoadMoreRow,
  ScrollBody,
  SectionStack,
  Wrapper,
} from "./KgCatalog.styles";
import { parseNodeTypeFromCanvasId } from "../../../services/kg/kgNodeIds";

export const KgCatalog: React.FC<KgCatalogProps> = ({
  nodes,
  selectedEntities,
  onEntityChange,
  selectedGroup,
  onGroupChange,
  disabledGroups,
  isRefreshing = false,
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
  loadedNodeCount = 0,
  totalNodeCount = 0,
  emptyMessage,
  hasActiveFilters = false,
  onNavigateToSection,
  onNodeClick,
  focusNodeId,
  selectedNodeId,
}) => {
  const [expandedSections, setExpandedSections] = useState<
    Set<KgCatalogSectionId>
  >(() => new Set());
  const [expandedTableIds, setExpandedTableIds] = useState<Set<string>>(
    () => new Set(),
  );
  const prevSelectedNodeIdRef = useRef<string | null>(null);
  const scrollBodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const prev = prevSelectedNodeIdRef.current;
    const now = selectedNodeId ?? null;
    prevSelectedNodeIdRef.current = now;

    if (prev === now) return;

    setExpandedTableIds((current) => {
      let next = current;

      if (now !== null && parseNodeTypeFromCanvasId(now) === "table") {
        if (!current.has(now)) {
          next = new Set(next);
          next.add(now);
        }
      }

      if (prev !== null && next.has(prev)) {
        if (next === current) next = new Set(next);
        next.delete(prev);
      }

      return next;
    });
  }, [selectedNodeId]);

  const grouped = useMemo(() => groupNodesByCatalogSection(nodes), [nodes]);

  // When focusNodeId changes, expand the containing section and scroll to the tile.
  useEffect(() => {
    if (!focusNodeId) return;
    const nodeType = parseNodeTypeFromCanvasId(focusNodeId);
    const sectionId = nodeType ? resolveCatalogSectionId(nodeType) : null;
    if (sectionId) {
      setExpandedSections((prev) => {
        if (prev.has(sectionId)) return prev;
        const next = new Set(prev);
        next.add(sectionId);
        return next;
      });
    }
    // Scroll after the section has expanded (one animation frame is enough).
    const raf = requestAnimationFrame(() => {
      const tile = scrollBodyRef.current?.querySelector(
        `[data-testid="kg-catalog-tile-${focusNodeId}"]`,
      );
      tile?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    return () => cancelAnimationFrame(raf);
  }, [focusNodeId]);

  const toggleTableTile = useCallback((nodeId: string) => {
    setExpandedTableIds((previous) => {
      const next = new Set(previous);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  const toggleSection = useCallback((sectionId: KgCatalogSectionId) => {
    setExpandedSections((previous) => {
      const next = new Set(previous);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  }, []);

  const loadMoreLabel =
    totalNodeCount > 0
      ? `Load more (${loadedNodeCount} of ${totalNodeCount} nodes)`
      : "Load more";

  return (
    <Wrapper data-testid="kg-catalog">
      <KgCanvasFilterBar
        variant="catalog"
        selectedEntities={selectedEntities}
        onEntityChange={onEntityChange}
        selectedGroup={selectedGroup}
        onGroupChange={onGroupChange}
        disabledGroups={disabledGroups}
        showLinks={false}
        onShowLinksChange={() => {}}
      />

      <ScrollBody ref={scrollBodyRef} $isRefreshing={isRefreshing} aria-busy={isRefreshing}>
        {!hasAnyCatalogNodes(grouped) && emptyMessage ? (
          <EmptyState caption={emptyMessage} />
        ) : (
          <SectionStack>
            {KG_CATALOG_SECTIONS.map((section) => {
              const SectionIcon = getKgCatalogSectionIcon(section.id);
              return (
                <KgCatalogSection
                  key={section.id}
                  section={section}
                  icon={<SectionIcon size={20} />}
                  nodes={grouped[section.id]}
                  isOpen={expandedSections.has(section.id)}
                  onToggle={() => toggleSection(section.id)}
                  onSectionExternalLink={() => onNavigateToSection(section.id)}
                  emptyMessage={getKgCatalogSectionEmptyMessage(
                    section.label,
                    hasActiveFilters,
                  )}
                  expandedTableIds={expandedTableIds}
                  onTableTileToggle={toggleTableTile}
                  onNodeClick={onNodeClick}
                  selectedNodeId={selectedNodeId}
                />
              );
            })}
          </SectionStack>
        )}
      </ScrollBody>

      {hasNextPage && onLoadMore ? (
        <LoadMoreRow>
          <LoadMoreButton
            type="button"
            onClick={onLoadMore}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? "Loading…" : loadMoreLabel}
          </LoadMoreButton>
        </LoadMoreRow>
      ) : null}
    </Wrapper>
  );
};

export default KgCatalog;
